// src/pages/Coleta/components/ColetasAtivas.jsx
import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, LinearProgress, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import * as Lucide from 'lucide-react';
import { dataDoExperimento, tempoDecorrido } from '../../../utils/coleta';
import { AZUL_CLARO } from '../../../theme/cores';

const MINUTO = 60_000;
const HORA = 60 * MINUTO;

/** "Acompanhar": ação de texto em negrito, no azul claro do layout. */
const estiloAcompanhar = { fontWeight: 700, color: AZUL_CLARO, '&:hover': { bgcolor: alpha(AZUL_CLARO, 0.08) } };

function Linha({ icone, cor, titulo, subtitulo, detalhe, progresso, acao, navegacao }) {
    return (
        <Paper variant="outlined" sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75, borderRadius: 3 }}>
            {/* Uma linha só a partir de 1200px: com a barra lateral aberta, no tablet o lado
                direito espremia o código da amostra até sumir. */}
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={{ xs: 1.25, lg: 2 }} alignItems={{ lg: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ p: 1, borderRadius: '50%', bgcolor: alpha(cor, 0.12), color: cor, display: 'flex', flexShrink: 0 }}>
                        {icone}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={700} noWrap>{titulo}</Typography>
                        <Typography variant="body2" color="text.secondary">{subtitulo}</Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center" justifyContent={{ xs: 'space-between', lg: 'flex-end' }}>
                    <Box sx={{ flexBasis: { xs: '100%', lg: 'auto' }, width: { lg: 240 }, minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                            {detalhe}
                        </Typography>
                        {progresso != null && (
                            <LinearProgress
                                variant="determinate"
                                value={progresso}
                                aria-label="Progresso estimado do ensaio"
                                sx={{
                                    mt: 0.75, height: 6, borderRadius: 3,
                                    bgcolor: alpha(cor, 0.15),
                                    '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: cor },
                                }}
                            />
                        )}
                    </Box>
                    {acao}
                    {navegacao}
                </Stack>
            </Stack>
        </Paper>
    );
}

/** Setas e posição do carrossel ("2 de 6"); circular, para ir passando. */
function Navegacao({ posicao, total, onAnterior, onProxima }) {
    return (
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
            <IconButton size="small" onClick={onAnterior} aria-label="Amostra anterior">
                <Lucide.ChevronLeft size={18} />
            </IconButton>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: 'center' }}>
                {posicao} de {total}
            </Typography>
            <IconButton size="small" onClick={onProxima} aria-label="Próxima amostra">
                <Lucide.ChevronRight size={18} />
            </IconButton>
        </Stack>
    );
}

/**
 * O que está acontecendo agora na página: amostras aguardando o tubo, ensaios em
 * andamento e experimentos que nasceram sem registro.
 *
 * Uma amostra em andamento por vez: no cliente costuma ser uma; com várias (o
 * laboratório da AUFTEK roda seis), as setas passam de uma em uma sem empurrar o
 * resto da página para baixo.
 */
export default function ColetasAtivas({
    ativas,
    semRegistro,
    rotuloMedida,
    nomePonto,
    podeAlterar,
    onCancelar,
    onRegistrarExperimento,
    onAbrir,
    duracaoEstimadaHoras,
    // Lista que junta paginas (painel da propriedade): cada item traz a sua cor, o nome
    // da pagina, a medida e a duracao estimada. Sem isto, valem as props da pagina.
    porItem,
}) {
    const theme = useTheme();
    const [agora, setAgora] = useState(() => new Date());

    // O "decorrido" anda sozinho enquanto há ensaio rodando.
    const temRodando = ativas.some((a) => a.status === 'EM_PROCESSAMENTO');
    useEffect(() => {
        if (!temRodando) return undefined;
        const id = setInterval(() => setAgora(new Date()), MINUTO);
        return () => clearInterval(id);
    }, [temRodando]);

    // Pela id, não pela posição: a recarga ao vivo pode reordenar a lista, e o
    // carrossel não deve pular para outra amostra sozinho.
    const [idAtual, setIdAtual] = useState(null);
    const indice = Math.max(0, ativas.findIndex((a) => a.id === idAtual));
    const passar = (passo) => setIdAtual(ativas[(indice + passo + ativas.length) % ativas.length].id);

    if (ativas.length === 0 && semRegistro.length === 0) return null;

    const navegacao =
        ativas.length > 1 ? (
            <Navegacao posicao={indice + 1} total={ativas.length} onAnterior={() => passar(-1)} onProxima={() => passar(1)} />
        ) : null;

    return (
        <Stack spacing={1.5}>
            {ativas.slice(indice, indice + 1).map((a) => {
                const ponto = nomePonto(a.coleta?.pontoColetaId);
                const extra = porItem ? porItem(a) || {} : {};
                const subtitulo = [extra.pagina, ponto, extra.rotuloMedida ?? rotuloMedida].filter(Boolean).join(' · ');
                const dispositivo = a.numeroSerie || 'dispositivo';
                if (a.status === 'PENDENTE') {
                    return (
                        <Linha
                            key={a.id}
                            icone={<Lucide.Hourglass size={20} />}
                            cor={theme.palette.warning.main}
                            titulo={a.identificacao}
                            subtitulo={subtitulo}
                            detalhe={`Aguardando o tubo em ${dispositivo}`}
                            acao={
                                <>
                                    {podeAlterar && (
                                        <Button size="small" color="inherit" onClick={() => onCancelar(a)}>
                                            Cancelar
                                        </Button>
                                    )}
                                    <Button size="small" onClick={() => onAbrir(a)} sx={estiloAcompanhar}>Acompanhar</Button>
                                </>
                            }
                            navegacao={navegacao}
                        />
                    );
                }
                const inicio = dataDoExperimento(a.experimento?.inicio);
                // Barra pela ponta ALTA da estimativa: o ensaio termina pelo crescimento,
                // não por relógio, então "cheia" quer dizer "já devia ter terminado".
                const [minH, maxH] = (extra.duracaoEstimadaHoras ?? duracaoEstimadaHoras) || [];
                const progresso =
                    inicio && maxH ? Math.min(100, Math.max(0, ((agora - inicio) / (maxH * HORA)) * 100)) : null;
                // "8–24 h" nunca quebra no meio: WORD JOINER (U+2060) em volta do traço
                // (o navegador quebra depois de "–") e espaço não separável antes do "h".
                const estimativa = minH && maxH ? ` · estimativa: ${minH}\u2060\u2013\u2060${maxH}\u00a0h` : '';
                return (
                    <Linha
                        key={a.id}
                        icone={<Lucide.Activity size={20} />}
                        cor={extra.cor || AZUL_CLARO}
                        titulo={a.identificacao}
                        subtitulo={subtitulo}
                        detalhe={
                            inicio
                                ? `${tempoDecorrido(inicio, agora)} decorridos${estimativa} · ${dispositivo}`
                                : `Em andamento · ${dispositivo}`
                        }
                        progresso={progresso}
                        acao={<Button size="small" onClick={() => onAbrir(a)} sx={estiloAcompanhar}>Acompanhar</Button>}
                        navegacao={navegacao}
                    />
                );
            })}

            {semRegistro.length > 0 && (
                <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, borderColor: 'warning.light', bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Lucide.Unlink size={18} color={theme.palette.warning.dark} />
                        <Typography fontWeight={700}>
                            {semRegistro.length === 1 ? '1 ensaio sem registro de coleta' : `${semRegistro.length} ensaios sem registro de coleta`}
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        O tubo entrou sem uma análise registrada antes. Registre os dados para a amostra
                        entrar no histórico e no gráfico.
                    </Typography>
                    <Stack spacing={1}>
                        {semRegistro.map((e) => {
                            const inicio = dataDoExperimento(e.inicio);
                            return (
                                <Stack key={e.id} direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>{e.nome || e.serialNumber}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {inicio ? `Início ${inicio.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}` : e.serialNumber}
                                            {String(e.status).toLowerCase() === 'active' ? ' · em andamento' : ''}
                                        </Typography>
                                    </Box>
                                    {podeAlterar && (
                                        <Button size="small" variant="outlined" onClick={() => onRegistrarExperimento(e)} sx={{ borderRadius: 2, flexShrink: 0 }}>
                                            Registrar dados
                                        </Button>
                                    )}
                                </Stack>
                            );
                        })}
                    </Stack>
                </Paper>
            )}
        </Stack>
    );
}
